import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../styles/datepicker-custom.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { Pagination } from "react-bootstrap";
import "./VirtualMeeting.css";
import { API_BASE_URL } from "../utils/constants";
import LoadingSpinner from "../components/LoadingSpinner";
import AddDetailsModal from "../components/AddDetailsModal";
import GlobalVideoUploadModal from "../components/GlobalVideoUploadModal";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useHearingSession } from "../hooks/useHearingSession";
import HearingLotSelect from "../components/HearingLotSelect";

// ── Fetch cases for third hearing ─────────────────────────────────────────────

const fetchThirdHearingCases = async (formattedDate, arbId) => {
  const response = await fetch(`${API_BASE_URL}/api/GetOppforNextHearing`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ Arb_id: arbId, Meeting_No: 3, Hearing_date: formattedDate }),
  });
  if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);
  const result = await response.json();
  const allCases = Array.isArray(result) ? result : (result.data || []);
  return allCases.map((item) => ({
    Case_id: item.caseId ?? item.Case_id,
    Reference_No: item.referenceNo ?? item.Reference_No,
    Cust_name: item.Cust_name,
    Third_hearing_date: item.thirdHearingDate ?? item.Third_hearing_date,
    detailsSaved: item.detailsSaved ?? false,
    lotNo: item.lotNo,
    clientId: item.clientId,
    productId: item.productId,
  }));
};

// ─────────────────────────────────────────────────────────────────────────────

const VirtualMeetingForThirdHearing = () => {
  const {
    selectedDate, formattedDate,
    availableDates, availableDateObjects, openToDate,
    filteredData, loading,
    currentItems, currentPage, setCurrentPage, totalPages, paginationPages,
    show, showVideoModal, setShowVideoModal,
    globalVideos, globalVideoInputRef, videosUploaded,
    lots, selectedLotKey, handleLotChange,
    handleDateChange, handleAppointments,
    handleAddDetails, handleClose, handleSaveDetails,
    handleGlobalVideoChange, handleRemoveGlobalVideo,
    handleCloseVideoModal, handleGlobalVideoUpload,
  } = useHearingSession({
    meetingNo: 3,
    availableDatesEndpoint: "AvailableThirdHearingDates",
    fetchCases: fetchThirdHearingCases,
  });

  if (loading) return <LoadingSpinner />;

  return (
    <div className="container mt-4">
      <h2>Virtual Meeting For Third Hearing</h2>

      <div className="row align-items-center my-5">
        <div className="col-md-2">
          <label htmlFor="datePicker" className="form-label">Select Date :-</label>
        </div>
        <div className="col-md-2">
          <DatePicker
            id="datePicker"
            selected={selectedDate}
            onChange={handleDateChange}
            includeDates={availableDateObjects}
            openToDate={openToDate}
            placeholderText="Select a date"
            dateFormat="MM/dd/yyyy"
            className="form-control date-picker-sm"
            disabled={availableDates.length === 0}
          />
        </div>
        {availableDates.length === 0 && (
          <div className="col-md-4">
            <small className="text-muted">No third hearing dates found</small>
          </div>
        )}
        {availableDates.length > 0 && (
          <div className="col-md-2">
            <button className="custBtn" onClick={handleAppointments}>
              Appointments
            </button>
          </div>
        )}
        <HearingLotSelect lots={lots} selectedLotKey={selectedLotKey} onChange={handleLotChange} />
        {filteredData.length > 0 && !videosUploaded && (
          <div className="col-md-2">
            <button className="custBtn" onClick={() => setShowVideoModal(true)}>
              Upload Videos
            </button>
          </div>
        )}
        {filteredData.length > 0 && videosUploaded && (
          <div className="col-md-2">
            <span className="text-success fw-semibold">Videos Uploaded</span>
          </div>
        )}
      </div>

      {/* Cases Table */}
      <div className="row">
        <div className="col-md-12">
          <div className="table-responsive">
            {currentItems.length > 0 && (
              <table
                className="table table-striped table-bordered table-hover mt-3 text-center"
                style={{ fontSize: "0.8rem", whiteSpace: "nowrap" }}
              >
                <thead>
                  <tr>
                    {videosUploaded && <th>Action</th>}
                    <th>Case ID</th>
                    <th>Reference No</th>
                    <th>Customer Name</th>
                    <th>Third Hearing Date</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((item, index) => (
                    <tr key={index}>
                      {videosUploaded && (
                        <td>
                          {item.detailsSaved
                            ? <span className="text-success fw-semibold">✓ Saved</span>
                            : <button className="custBtn" onClick={() => handleAddDetails(item.Case_id)}>Assign to Contested</button>
                          }
                        </td>
                      )}
                      <td>{item.Case_id}</td>
                      <td>{item.Reference_No}</td>
                      <td>{item.Cust_name}</td>
                      <td>{item.Third_hearing_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {totalPages > 1 && (
            <Pagination className="justify-content-center">
              <Pagination.Prev onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))} />
              {paginationPages.map(({ n, active }) => (
                <Pagination.Item key={n} active={active} onClick={() => setCurrentPage(n)}>{n}</Pagination.Item>
              ))}
              <Pagination.Next onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))} />
            </Pagination>
          )}
        </div>
      </div>

      {/* ── Add Details Modal ──────────────────────────────────────────────────── */}
      <AddDetailsModal show={show} onHide={handleClose} onSave={handleSaveDetails} />

      {/* ── Upload Videos Modal ───────────────────────────────────────────────── */}
      <GlobalVideoUploadModal
        show={showVideoModal}
        onHide={handleCloseVideoModal}
        videoInputRef={globalVideoInputRef}
        videos={globalVideos}
        onVideoAdd={handleGlobalVideoChange}
        onVideoRemove={handleRemoveGlobalVideo}
        onUpload={handleGlobalVideoUpload}
        formattedDate={formattedDate}
        hearingLabel="third"
      />

      <ToastContainer />
    </div>
  );
};

export default VirtualMeetingForThirdHearing;
